import type { Difficulty } from "@/domain";

import { createHabitsLevel } from "./create-habits-level";

export interface RoutineItem {
  id: string;
  label: string;
  audio: string;
}

export interface DailyRoutinePayload {
  items: RoutineItem[];
  correctOrder: string[];
}

type RoutineItemInput = Omit<RoutineItem, "audio">;

function withRoutineAudio(
  difficulty: Difficulty,
  payload: Omit<DailyRoutinePayload, "items"> & {
    items: RoutineItemInput[];
  }
): DailyRoutinePayload {
  return {
    ...payload,
    items: payload.items.map((item) => ({
      ...item,
      audio:
        "/audio/habits/daily-routine-" +
        String(difficulty) +
        "-" +
        item.id +
        ".ogg"
    }))
  };
}

export const dailyRoutineLevels = [
  createHabitsLevel<DailyRoutinePayload>({
    activityType: "daily-routine",
    difficulty: 1,
    objectives: ["练习有前后依赖的穿鞋步骤", "独立完成出门前的自理准备"],
    title: "穿好鞋再出门",
    instruction: "袜子和鞋在门边等你。先穿袜子，再穿鞋，最后粘好鞋面搭扣，把卡片排排好。",
    payload: withRoutineAudio(1, {
      items: [
        { id: "put-on-socks", label: "先把袜子穿好" },
        { id: "put-on-shoes", label: "袜子穿好后，把双脚穿进鞋里" },
        { id: "fasten-shoes", label: "双脚穿进鞋后，粘好鞋面搭扣" }
      ],
      correctOrder: ["put-on-socks", "put-on-shoes", "fasten-shoes"]
    })
  }),
  createHabitsLevel<DailyRoutinePayload>({
    activityType: "daily-routine",
    difficulty: 2,
    objectives: ["掌握完整的洗手步骤", "养成餐前洗手的卫生习惯"],
    title: "小手洗干净",
    instruction: "小手要洗得干干净净。先打湿双手，最后冲净擦干，把中间的步骤也排好。",
    payload: withRoutineAudio(2, {
      items: [
        { id: "wet-hands", label: "先用流动水打湿双手" },
        { id: "add-soap", label: "双手打湿后，抹上洗手液" },
        { id: "rub-hands", label: "有洗手液后，认真搓洗手心、手背和指缝" },
        { id: "rinse-and-dry", label: "搓洗完成后，冲洗干净并擦干" }
      ],
      correctOrder: ["wet-hands", "add-soap", "rub-hands", "rinse-and-dry"]
    })
  }),
  createHabitsLevel<DailyRoutinePayload>({
    activityType: "daily-routine",
    difficulty: 3,
    objectives: ["按完整流程刷到牙齿各面", "刷完后清洁并收好牙刷"],
    title: "认真刷好牙",
    instruction: "牙刷和牙膏准备好啦。先挤牙膏，刷完还要把牙刷洗净放好，再把卡片排好。",
    payload: withRoutineAudio(3, {
      items: [
        { id: "prepare-toothbrush", label: "先在牙刷上挤少量牙膏" },
        {
          id: "brush-all-surfaces",
          label: "牙膏准备好后，轻轻刷牙齿外侧、内侧和咀嚼面"
        },
        {
          id: "finish-and-store",
          label: "每一面都刷过后，吐出泡沫，冲净牙刷并放回原处"
        }
      ],
      correctOrder: [
        "prepare-toothbrush",
        "brush-all-surfaces",
        "finish-and-store"
      ]
    })
  }),
  createHabitsLevel<DailyRoutinePayload>({
    activityType: "daily-routine",
    difficulty: 4,
    objectives: ["完成放学后的水壶清洁", "根据物品状态判断下一步"],
    title: "回家收好水壶",
    instruction: "水壶还装着一点水，正待在关好的书包里。先打开书包，最后把水壶洗净晾干。",
    payload: withRoutineAudio(4, {
      items: [
        { id: "open-schoolbag", label: "先把书包放稳并拉开拉链" },
        { id: "take-out-bottle", label: "书包打开后，取出水壶" },
        { id: "empty-bottle", label: "水壶取出后，把剩水倒进水池" },
        { id: "wash-bottle", label: "水壶倒空后，用清水洗净水壶" },
        { id: "dry-bottle", label: "水壶洗净后，倒扣在架子上晾干" }
      ],
      correctOrder: [
        "open-schoolbag",
        "take-out-bottle",
        "empty-bottle",
        "wash-bottle",
        "dry-bottle"
      ]
    })
  }),
  createHabitsLevel<DailyRoutinePayload>({
    activityType: "daily-routine",
    difficulty: 5,
    objectives: ["根据第二天的安排准备书本", "用检查结果完成书包整理"],
    title: "按安排装好书包",
    instruction: "明天的安排卡在桌上，空书包正等着装东西。照着安排卡准备书本，再排好步骤。",
    payload: withRoutineAudio(5, {
      items: [
        { id: "read-plan-card", label: "先看安排卡，知道明天要用哪些书" },
        { id: "find-needed-books", label: "知道书目后，把需要的书找齐" },
        {
          id: "match-books-to-plan",
          label: "书找齐后，让每本书和安排卡上的活动一一对应"
        },
        {
          id: "pack-checked-books",
          label: "每本书都对应好后，把这些书放进书包"
        },
        {
          id: "zip-ready-bag",
          label: "书都装好后，拉好书包拉链"
        },
        {
          id: "place-ready-bag",
          label: "拉链拉好后，把书包放到固定位置"
        }
      ],
      correctOrder: [
        "read-plan-card",
        "find-needed-books",
        "match-books-to-plan",
        "pack-checked-books",
        "zip-ready-bag",
        "place-ready-bag"
      ]
    })
  }),
  createHabitsLevel<DailyRoutinePayload>({
    activityType: "daily-routine",
    difficulty: 6,
    objectives: ["完成到校后的用品准备", "按书包状态判断下一步"],
    title: "到教室后准备好",
    instruction: "到教室啦，书包还背在身上。先放好书包，最后坐到座位上等老师，把卡片排好。",
    payload: withRoutineAudio(6, {
      items: [
        { id: "place-bag-on-desk", label: "先把背着的书包放稳在桌面上" },
        { id: "open-bag", label: "书包放稳后，拉开拉链" },
        {
          id: "take-out-supplies",
          label: "书包打开后，取出第一节活动要用的物品"
        },
        { id: "close-bag", label: "用品取出后，拉好书包拉链" },
        {
          id: "store-bag",
          label: "拉链拉好后，把书包放进自己的收纳格"
        },
        { id: "sit-ready", label: "书包收好后，把用品摆好并坐到座位上等老师" }
      ],
      correctOrder: [
        "place-bag-on-desk",
        "open-bag",
        "take-out-supplies",
        "close-bag",
        "store-bag",
        "sit-ready"
      ]
    })
  })
];
